import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Spin,
  Card,
  Drawer,
  Modal,
  Upload,
  Tag,
  Space,
  Input,
  Descriptions,
  Divider,
  Row,
  Col,
  Typography,
  List,
  Avatar,
  Collapse,
  Statistic,
  Select,
  Progress,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  FilePdfOutlined,
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
  InboxOutlined,
  SearchOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined,
  FileZipOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Header166 from "../header/header";
import SalidaBodega_v2 from "./SalidaBodega_v2";
import ActaEntregaPDF from "./FormatoAcataEntragaSostenibilidad";
import config from "../../config";
import JSZip from "jszip";
import {
  deleteSalida,
  getsalidas,
  uploadFormatoEntrega,
} from "../../services/entregas/servicesEntregas";

const { Dragger } = Upload;
const { Search } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;
const baseUrl = config.baseUrl;

const TablaSalidas = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dataPDF, setDataPDF] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerRegistroVisible, setDrawerRegistroVisible] = useState(false);
  const [drawerPdfVisible, setDrawerPdfVisible] = useState(false);
  const [modalUploadVisible, setModalUploadVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchType, setSearchType] = useState("codActa"); // 'codActa' o 'codigoInterno'
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [recordUpdate, setRecordUpdate] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Función para cargar los datos
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const response = await getsalidas();

      // Verificar si la respuesta tiene la estructura esperada
      if (response && response.data) {
        setData(response.data);
        setFilteredData(response.data);
      } else if (Array.isArray(response)) {
        setData(response);
        setFilteredData(response);
      } else {
        message.error("Formato de datos inesperado");
        setData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error al cargar datos:", error);
      message.error("Error al cargar las salidas de sostenibilidad");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDatos();
  }, []);

  // Función para filtrar por código de acta o código interno
  const handleSearch = (value) => {
    setSearchText(value);
    if (!value) {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => {
        if (searchType === "codActa") {
          return (
            item.codActa &&
            item.codActa.toLowerCase().includes(value.toLowerCase())
          );
        } else if (searchType === "codigoInterno") {
          const codigoInterno =
            item.jsonDetalleSalida?.proyecto?.codigo_interno;
          return (
            codigoInterno &&
            codigoInterno.toLowerCase().includes(value.toLowerCase())
          );
        }
        return false;
      });
      setFilteredData(filtered);
    }
  };

  // Función para cambiar el tipo de búsqueda
  const handleSearchTypeChange = (value) => {
    setSearchType(value);
    // Re-aplicar filtro con el nuevo tipo
    if (searchText) {
      handleSearch(searchText);
    }
  };

  // Función para descargar archivo individual (solo para un archivo)
  const downloadSingleFile = async (url, filename) => {
    try {
      const response = await fetch(`${baseUrl}/${url}?ts=${Date.now()}`);

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      return { success: true };
    } catch (error) {
      console.error(`Error downloading ${filename}:`, error);
      return { success: false, error };
    }
  };

  // Función para obtener blob de archivo (para ZIP)
  const fetchFileBlob = async (url, filename) => {
    try {
      const response = await fetch(`${baseUrl}/${url}?ts=${Date.now()}`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      return { success: true, blob, filename };
    } catch (error) {
      console.error(`Error fetching ${filename}:`, error);
      return { success: false, error };
    }
  };

  // Función para crear y descargar ZIP
  const createAndDownloadZip = async (files) => {
    // Nota: Necesitarás instalar jszip: npm install jszip
    // import JSZip from 'jszip';

    try {
      // Importar JSZip dinámicamente si no está disponible globalmente
      const zip = new JSZip();

      // Añadir archivos al ZIP
      files.forEach(({ blob, filename }) => {
        if (blob) {
          zip.file(filename, blob);
        }
      });

      // Generar el ZIP
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Descargar el ZIP
      const blobUrl = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `actas_entrega_${
        new Date().toISOString().split("T")[0]
      }.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      console.error("Error creating ZIP:", error);
      message.error(
        "Error al crear el archivo ZIP. Asegúrate de que jszip esté instalado."
      );
      return false;
    }
  };

  // Función para descargar archivos seleccionados
  const handleDownloadSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.error("Selecciona al menos un registro para descargar");
      return;
    }

    const selectedRecords = data.filter((record) =>
      selectedRowKeys.includes(record.oid)
    );

    // Filtrar solo los registros que tienen baseUrl
    const recordsWithFiles = selectedRecords.filter(
      (record) => record.baseUrl && record.baseUrl.trim() !== ""
    );

    if (recordsWithFiles.length === 0) {
      message.error(
        "Los registros seleccionados no tienen archivos disponibles para descargar"
      );
      return;
    }

    setDownloadLoading(true);
    setDownloadProgress(0);

    try {
      if (recordsWithFiles.length === 1) {
        // Un solo archivo - descarga directa (sin crear ZIP)
        const record = recordsWithFiles[0];
        const filename = `formato_entrega_${record.codActa}.pdf`;
        const result = await downloadSingleFile(record.baseUrl, filename);

        if (result.success) {
          message.success("Archivo descargado exitosamente");
        } else {
          message.error("Error al descargar el archivo");
        }
      } else {
        // Múltiples archivos - solo crear y descargar ZIP (sin descargas individuales)
        message.loading("Preparando archivos para ZIP...", 0);

        const fetchPromises = recordsWithFiles.map(async (record, index) => {
          const filename = `formato_entrega_${record.codActa}.pdf`;
          const result = await fetchFileBlob(record.baseUrl, filename);

          // Actualizar progreso
          const progress = ((index + 1) / recordsWithFiles.length) * 90; // 90% para fetch, 10% para crear ZIP
          setDownloadProgress(progress);

          return result;
        });

        const results = await Promise.all(fetchPromises);
        const successfulFetches = results.filter((result) => result.success);

        message.destroy(); // Destruir el mensaje de loading

        if (successfulFetches.length > 0) {
          setDownloadProgress(95);
          const zipCreated = await createAndDownloadZip(successfulFetches);

          if (zipCreated) {
            setDownloadProgress(100);
            message.success(
              `ZIP descargado exitosamente con ${successfulFetches.length} archivo(s)`
            );
          }
        }

        const failedFetches = results.filter((result) => !result.success);
        if (failedFetches.length > 0) {
          message.warning(
            `${failedFetches.length} archivo(s) no se pudieron incluir en el ZIP`
          );
        }
      }
    } catch (error) {
      message.destroy();
      console.error("Error en descarga masiva:", error);
      message.error("Error durante la descarga");
    } finally {
      setDownloadLoading(false);
      setDownloadProgress(0);
      setSelectedRowKeys([]);
    }
  };

  // Función para manejar la visualización del PDF
  const handleVerPDF = (record) => {
    const dataProyecto = record.jsonDetalleSalida.proyecto;
    const dataEntrega = record.jsonDetalleSalida.entrega;

    const initialFormData = {
      implementador: dataProyecto.implementador,
      sigla: dataProyecto.sigla,
      codigo_pre: dataProyecto.codPre,
      proyecto_productivo: dataProyecto.nombre_proyecto,
      direccion_proyecto_productivo: dataProyecto.direccion_proyecto_productivo,
      linea_productiva: dataProyecto.linea_productiva,
      fecha_entrega_parcial: dataEntrega.fecha_entrega,
      fecha_entrega_final: "",
      grupo_territorial: dataProyecto.territorio.grupo.nombre,
      departamento: dataProyecto.territorio.departamento.nombre,
      municipio: dataProyecto.territorio.municipio.nombre,
      numero_personas_reincorporacion: dataEntrega.lista_participantes.length,
      numero_personas_comunidad: "",
      lista_participantes: dataEntrega.lista_participantes,
      lista_insumos: dataEntrega.balance_items,
      valorTotalEntrega: dataEntrega.valorTotalEntrega,
      sitio_entrega: "",
      codigo_interno: record.codActa,
      seriales: dataEntrega.seriales_globales,
      valor_total: dataEntrega.valor_total,
      dia: dataEntrega.fechaEntrega.dia,
      mes: dataEntrega.fechaEntrega.mes,
      vigencia: dataEntrega.fechaEntrega.vigencia,
      personas_reincorporacion: dataProyecto.personas_reincorporacion,
      personas_comunidad: dataProyecto.personas_comunidad,
    };

    setDataPDF(initialFormData);
    setSelectedRecord(record);
    setDrawerPdfVisible(true);
  };

  // Función para ver el detalle completo
  const handleVerDetalle = (record) => {
    setSelectedRecord(record);
    setModalDetalleVisible(true);
  };

  // Función para abrir el drawer de registro
  const handleNuevaEntrega = () => {
    setDrawerRegistroVisible(true);
    setIsEditing(false);
    setRecordUpdate([]);
  };

  // Función para abrir modal de carga de documento
  const handleCargarDocumento = (record) => {
    setSelectedRecord(record);
    setModalUploadVisible(true);
    setFileList([]);
  };

  // Función para cerrar drawers y modales
  const handleCloseDrawers = () => {
    setDrawerRegistroVisible(false);
    setDrawerPdfVisible(false);
    setSelectedRecord(null);
    setDataPDF([]);
  };

  const handleCloseUploadModal = () => {
    setModalUploadVisible(false);
    setSelectedRecord(null);
    setFileList([]);
    setUploadLoading(false);
  };

  const handleCloseDetalleModal = () => {
    setModalDetalleVisible(false);
    setSelectedRecord(null);
  };

  // Configuración del componente Upload
  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".pdf",
    fileList: fileList,
    beforeUpload: (file) => {
      // Validar que sea un PDF
      const isPDF = file.type === "application/pdf";
      if (!isPDF) {
        message.error("Solo se permiten archivos PDF");
        return false;
      }

      // Validar tamaño (ejemplo: máximo 10MB)
      const isLt10M = file.size / 1024 / 1024 < 50;
      if (!isLt10M) {
        message.error("El archivo debe ser menor a 50MB");
        return false;
      }

      // Solo permitir un archivo
      if (fileList.length >= 1) {
        message.error("Solo se permite cargar un documento");
        return false;
      }

      return false; // Prevenir upload automático
    },
    onChange: (info) => {
      let newFileList = [...info.fileList];

      // Limitar a un solo archivo
      newFileList = newFileList.slice(-1);

      setFileList(newFileList);
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  // Función para enviar el documento
  const handleUploadDocument = async () => {
    if (fileList.length === 0) {
      message.error("Debe seleccionar un archivo");
      return;
    }

    setUploadLoading(true);

    try {
      const archivo = fileList[0].originFileObj;
      const idRegistro = selectedRecord.oid;
      const estado = "1";

      const response = await uploadFormatoEntrega(idRegistro, estado, archivo);

      if (response.success) {
        message.success("Documento cargado exitosamente");
        handleCloseUploadModal();
        await cargarDatos();
      } else {
        message.error(response.message || "Error al cargar el documento");
      }
    } catch (error) {
      console.error("Error al cargar documento:", error);
      message.error("Error al cargar el documento");
    } finally {
      setUploadLoading(false);
    }
  };

  // Función para renderizar el estado
  const renderEstado = (estado) => {
    if (estado == 1) {
      return <Tag color="success">Registrado con firma</Tag>;
    } else {
      return <Tag color="warning">Registrado sin firma</Tag>;
    }
  };

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Función para formatear moneda
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(valor);
  };

  // Componente para renderizar el detalle del proyecto
  const renderDetalleProyecto = (detalle) => {
    if (!detalle || !detalle.proyecto) return null;

    const { proyecto, entrega } = detalle;

    return (
      <div>
        <Collapse defaultActiveKey={["1", "2", "3", "4"]} ghost>
          {/* Información del Proyecto */}
          <Panel
            header={
              <Title level={5} style={{ margin: 0 }}>
                <InfoCircleOutlined
                  style={{ marginRight: 8, color: "#1890ff" }}
                />
                Información del Proyecto
              </Title>
            }
            key="1"
          >
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Nombre del Proyecto" span={2}>
                <Text strong>{proyecto.nombre_proyecto}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Código PRE">
                <Text code>{proyecto.codPre}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Código Interno">
                <Text code>{proyecto.codigo_interno}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Línea Productiva">
                <Tag color="blue">{proyecto.linea_productiva}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Sector">
                <Tag color="green">{proyecto.sector}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Implementador" span={2}>
                {proyecto.implementador}
              </Descriptions.Item>
              <Descriptions.Item label="Profesional GSE" span={2}>
                {proyecto.nombre_profesional_gse}
              </Descriptions.Item>
              <Descriptions.Item label="Memorando">
                <Text code>{proyecto.memorando}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Asignación">
                {formatearFecha(proyecto.fechaAsignacion)}
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {/* Información Territorial */}
          <Panel
            header={
              <Title level={5} style={{ margin: 0 }}>
                <InfoCircleOutlined
                  style={{ marginRight: 8, color: "#52c41a" }}
                />
                Información Territorial
              </Title>
            }
            key="2"
          >
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Grupo Territorial" span={2}>
                <Text strong>{proyecto.territorio.grupo.nombre}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Departamento">
                {proyecto.territorio.departamento.nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Municipio">
                {proyecto.territorio.municipio.nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Código DANE Municipio">
                <Text code>{proyecto.territorio.municipio.codigo_dane}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Código DANE Depto">
                <Text code>{proyecto.territorio.departamento.codigo_dane}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Panel>

          {/* Información de Entrega */}
          <Panel
            header={
              <Title level={5} style={{ margin: 0 }}>
                <ShoppingCartOutlined
                  style={{ marginRight: 8, color: "#faad14" }}
                />
                Información de Entrega
              </Title>
            }
            key="3"
          >
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Fecha de Entrega">
                <Text strong style={{ color: "#1890ff" }}>
                  {formatearFecha(entrega.fecha_entrega)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total">
                <Statistic
                  value={entrega.valor_total}
                  formatter={(value) => formatearMoneda(value)}
                  valueStyle={{
                    color: "#52c41a",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Cantidad Total Items">
                <Tag
                  color="blue"
                  style={{ fontSize: "14px", padding: "4px 12px" }}
                >
                  {entrega.cantidad_total_items} items
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total Participantes">
                <Tag
                  color="purple"
                  style={{ fontSize: "14px", padding: "4px 12px" }}
                >
                  {entrega.lista_participantes?.length || 0} personas
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Balance de Items */}
            <Divider orientation="left">
              <Text strong>
                Balance de Items ({entrega.balance_items?.length || 0})
              </Text>
            </Divider>
            <List
              size="small"
              bordered
              dataSource={entrega.balance_items || []}
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size="small"
                        style={{ backgroundColor: "#1890ff" }}
                      >
                        {item.item || index + 1}
                      </Avatar>
                    }
                    title={<Text strong>{item.descripcion}</Text>}
                    description={
                      <Space wrap size="middle">
                        <Tag color="blue">Cantidad: {item.cantidad}</Tag>
                        <Tag color="green">Unidad: {item.unidad}</Tag>
                        {item.valor_unitario && (
                          <Tag color="gold">
                            Valor Unit.: {formatearMoneda(item.valor_unitario)}
                          </Tag>
                        )}
                        {item.valor_unitario && item.cantidad && (
                          <Tag color="red">
                            Valor Total:{" "}
                            {formatearMoneda(
                              item.valor_unitario * item.cantidad
                            )}
                          </Tag>
                        )}
                        {item.serial !== "N/A" && (
                          <Tag color="purple">Serial: {item.serial}</Tag>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Panel>

          {/* Lista de Participantes */}
          <Panel
            header={
              <Title level={5} style={{ margin: 0 }}>
                <UserOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                Lista de Participantes (
                {entrega.lista_participantes?.length || 0})
              </Title>
            }
            key="4"
          >
            <List
              size="small"
              bordered
              dataSource={entrega.lista_participantes || []}
              renderItem={(participante, index) => (
                <List.Item
                  style={{
                    borderRadius: "6px",
                    marginBottom: "8px",
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={<UserOutlined />}
                        style={{ backgroundColor: "#722ed1" }}
                        size="large"
                      />
                    }
                    title={
                      <Text strong style={{ fontSize: "15px" }}>
                        {participante.nombre_completo}
                      </Text>
                    }
                    description={
                      <Space wrap size="small">
                        <Tag color="purple" style={{ fontWeight: "500" }}>
                          SIRR: {participante.codigo_sirr}
                        </Tag>
                        {participante.identificacion && (
                          <Tag color="geekblue" style={{ fontWeight: "500" }}>
                            ID: {participante.identificacion}
                          </Tag>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Panel>
        </Collapse>
      </div>
    );
  };

  // Configuración para selección de filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.baseUrl || record.baseUrl.trim() === "",
      name: record.codActa,
    }),
  };

  // 🗑️ eliminar
  const handleDelete = async (oid) => {
    try {
      // deleteBodega debe devolver { status, message }
      const resp = await deleteSalida(oid);

      if (resp.success) {
        message.success(resp.message);
        cargarDatos();
      } else {
        message.error("No se pudo eliminar la bodega.");
      }
    } catch (error) {
      message.error("No se pudo eliminar la bodega.");
    }
  };

  // 🗑️ Actualizar
  const handleActualizar = async (record) => {
    setDrawerRegistroVisible(true);
    setIsEditing(true);
    setRecordUpdate(record || []);
  };

  // Definición de columnas de la tabla
  const columns = [
    {
      title: "Acciones",
      key: "acciones",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space size="middle" wrap>
          <Tooltip title="Actualizar entrega">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              shape="circle"
              onClick={() => handleActualizar(record)}
            />
          </Tooltip>

          <Popconfirm
            title="¿Eliminar esta entrega?"
            onConfirm={() => handleDelete(record.oid)}
            okText="Sí"
            cancelText="No"
          >
            <Tooltip title="Eliminar entrega">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                shape="circle"
              />
            </Tooltip>
          </Popconfirm>

          <Tooltip title="Ver PDF">
            <Button
              type="default"
              icon={<FilePdfOutlined />}
              size="small"
              shape="circle"
              onClick={() => {
                if (record.baseUrl) {
                  window.open(
                    `${baseUrl}/${record.baseUrl}?ts=${Date.now()}`,
                    "_blank"
                  );
                } else {
                  handleVerPDF(record);
                }
              }}
            />
          </Tooltip>

          <Tooltip title="Ver detalle">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              shape="circle"
              onClick={() => handleVerDetalle(record)}
            />
          </Tooltip>

          {record.estado == 0 && (
            <Tooltip title="Cargar documento">
              <Button
                type="dashed"
                icon={<UploadOutlined />}
                size="small"
                shape="circle"
                onClick={() => handleCargarDocumento(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Código Interno",
      dataIndex: ["jsonDetalleSalida", "proyecto", "codigo_interno"],
      key: "codigo_interno",
      width: 120,
    },
    {
      title: "Código Acta",
      dataIndex: "codActa",
      key: "codActa",
      width: 120,
      sorter: (a, b) => a.codActa.localeCompare(b.codActa),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 150,
      render: (estado) => renderEstado(estado),
      sorter: (a, b) => a.estado - b.estado,
      filters: [
        { text: "Registrado con firma", value: 1 },
        { text: "Registrado sin firma", value: 0 },
      ],
      onFilter: (value, record) => record.estado === value,
    },

    {
      title: "Sigla",
      key: "sigla",
      dataIndex: "sigla", // opcional, si lo usas en tus datos
      width: 250,
      ellipsis: true,
      render: (_, record) => {
        const sigla = record?.jsonDetalleSalida?.proyecto?.sigla || "-";

        return sigla;
      },
    },
    {
      title: "Organización",
      key: "implementador",
      dataIndex: "implementador", // opcional, si lo usas en tus datos
      width: 250,
      ellipsis: true,
      render: (_, record) => {
        const implementador =
          record?.jsonDetalleSalida?.proyecto?.implementador || "-";

        return (
          <Tooltip title={implementador}>
            <span
              style={{
                display: "block",
                width: "100%",
                maxWidth: 250, // un poco menos que el width de la columna
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {implementador}
            </span>
          </Tooltip>
        );
      },
    },

    {
      title: "Valor Total",
      dataIndex: "valor_total",
      key: "valor_total",
      width: 120,
      render: (valor) => formatearMoneda(valor),
      sorter: (a, b) => a.valor_total - b.valor_total,
    },
    {
      title: "Fecha Entrega",
      key: "fecha_entrega",
      width: 150,
      render: (_, record) => {
        return (
          record?.jsonDetalleSalida?.entrega?.fechaEntrega?.fechaCompleta || "-"
        );
      },
    },
    {
      title: "Responsable",
      dataIndex: "oidResponsable",
      key: "oidResponsable",
      width: 150,
    },
  ];

  return (
    <div className="container">
      <Header166 type="salidabodega" />
      <Card
        style={{
          margin: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            marginBottom: 24,
            padding: "24px",
            background: "#fafafa",
            borderRadius: "8px",
            border: "1px solid #f0f0f0",
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6} lg={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNuevaEntrega}
                size="large"
                block
                style={{
                  height: "44px",
                  fontWeight: "600",
                  borderRadius: "6px",
                  boxShadow: "0 2px 8px rgba(24, 144, 255, 0.2)",
                }}
              >
                Nueva Entrega
              </Button>
            </Col>

            <Col xs={24} sm={12} md={6} lg={4}>
              <Button
                type="default"
                icon={
                  downloadLoading ? <Spin size="small" /> : <FileZipOutlined />
                }
                onClick={handleDownloadSelected}
                size="large"
                block
                disabled={selectedRowKeys.length === 0 || downloadLoading}
                loading={downloadLoading}
                style={{
                  height: "44px",
                  fontWeight: "600",
                  borderRadius: "6px",
                  borderColor: "#52c41a",
                  color: "#52c41a",
                }}
              >
                Descargar ({selectedRowKeys.length})
              </Button>
            </Col>

            <Col xs={24} sm={24} md={12} lg={8}>
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  value={searchType}
                  onChange={handleSearchTypeChange}
                  style={{ width: 140 }}
                  size="large"
                >
                  <Option value="codActa">Cód. Acta</Option>
                  <Option value="codigoInterno">Cód. Interno</Option>
                </Select>
                <Search
                  placeholder={`Buscar por ${
                    searchType === "codActa"
                      ? "código de acta"
                      : "código interno"
                  }...`}
                  allowClear
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  size="large"
                  style={{ borderRadius: "6px" }}
                  enterButton={
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      style={{
                        height: "44px",
                        borderRadius: "0 6px 6px 0",
                      }}
                    >
                      Buscar
                    </Button>
                  }
                />
              </Space.Compact>
            </Col>

            <Col xs={24} sm={24} md={24} lg={8}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <Tag
                  color="processing"
                  style={{
                    fontSize: "13px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    border: "none",
                  }}
                >
                  Total: {data.length} registros
                </Tag>
                <Tag
                  color="success"
                  style={{
                    fontSize: "13px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    border: "none",
                  }}
                >
                  Mostrando: {filteredData.length}
                </Tag>
                {selectedRowKeys.length > 0 && (
                  <Tag
                    color="blue"
                    style={{
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "16px",
                    }}
                  >
                    Seleccionados: {selectedRowKeys.length}
                  </Tag>
                )}
                {searchText && (
                  <Tag
                    color="warning"
                    closable
                    onClose={() => handleSearch("")}
                    style={{
                      fontSize: "12px",
                      padding: "4px 8px",
                      borderRadius: "16px",
                    }}
                  >
                    Filtro: "{searchText}"
                  </Tag>
                )}
              </div>
            </Col>
          </Row>

          {/* Barra de progreso para descarga */}
          {downloadLoading && downloadProgress > 0 && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                <Progress
                  percent={downloadProgress}
                  status={downloadProgress === 100 ? "success" : "active"}
                  showInfo={true}
                  format={(percent) => `${Math.round(percent)}%`}
                />
                <Text
                  type="secondary"
                  style={{ fontSize: "12px", marginTop: 4, display: "block" }}
                >
                  Descargando archivos seleccionados...
                </Text>
              </Col>
            </Row>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="oid"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ y: 500, x: "max-content" }}
          pagination={{
            current: currentPage, // página actual
            pageSize: pageSize, // filas por página
            total: filteredData.length, // total de registros
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} registros`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          size="middle"
        />
      </Card>

      {/* Drawer para Registro de Nueva Entrega */}
      <Drawer
        title={isEditing ? "Editar Entrega" : "Registrar Nueva Entrega"}
        placement="right"
        onClose={handleCloseDrawers}
        open={drawerRegistroVisible}
        width="100%"
        style={{ maxWidth: "100vw" }}
        destroyOnClose
        closeIcon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
      >
        <SalidaBodega_v2
          cargarDatos={cargarDatos}
          isEditing={isEditing}
          recordUpdate={recordUpdate}
          closeModal={() => setDrawerRegistroVisible(false)} // ✅ Correcto
        />
      </Drawer>

      {/* Drawer para Visualización de PDF */}
      <Drawer
        title={`PDF - Acta: ${selectedRecord?.codActa || ""}`}
        placement="right"
        onClose={handleCloseDrawers}
        open={drawerPdfVisible}
        width="100%"
        style={{ maxWidth: "100vw" }}
        destroyOnClose
        closeIcon={<CloseOutlined style={{ color: "white", fontSize: 18 }} />}
      >
        <ActaEntregaPDF {...dataPDF} />
      </Drawer>

      {/* Modal para Ver Detalle */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: "#1890ff" }} />
            {`Detalle Completo - Acta: ${selectedRecord?.codActa || ""}`}
          </Space>
        }
        open={modalDetalleVisible}
        onCancel={handleCloseDetalleModal}
        footer={[
          <Button key="close" type="primary" onClick={handleCloseDetalleModal}>
            Cerrar
          </Button>,
        ]}
        width="90%"
        style={{ maxWidth: 1200, top: 20 }}
        destroyOnClose
      >
        {selectedRecord &&
          renderDetalleProyecto(selectedRecord.jsonDetalleSalida)}
      </Modal>

      {/* Modal para Cargar Documento */}
      <Modal
        title={`Cargar Documento - Acta: ${selectedRecord?.codActa || ""}`}
        open={modalUploadVisible}
        onCancel={handleCloseUploadModal}
        footer={[
          <Button key="cancel" onClick={handleCloseUploadModal}>
            Cancelar
          </Button>,
          <Button
            key="upload"
            type="primary"
            loading={uploadLoading}
            onClick={handleUploadDocument}
            disabled={fileList.length === 0}
          >
            Cargar Documento
          </Button>,
        ]}
        width={600}
        destroyOnClose
      >
        <div style={{ padding: "20px 0" }}>
          <p style={{ marginBottom: 16 }}>
            Solo se permite cargar un documento en formato PDF.
          </p>
          <Dragger {...uploadProps} style={{ padding: 20 }}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Haz clic o arrastra el archivo a esta área para cargarlo
            </p>
            <p className="ant-upload-hint">Solo archivo PDF.</p>
          </Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default TablaSalidas;
